export const COMPANY = {
  name: "K&G Mowers and Tractors",
  address: "623 N Main St, Paris, TX 75460",
  phone: "(430) 900-1151",
  rfpEmail: "Ike@kgmowers.com",
};

export const fmtMoney = (n) =>
  (isFinite(n) ? n : 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });

export const DEFAULT_COST_INPUTS = {
  mpg: 11,
  fuelPrice: 5.044,
  wage: 17,
  avgSpeed: 60,
  wearTear: 0.2,
  stopTimeMin: 20,
  marginPct: 25,
  brokerRatePerMile: 3,
  brokerMarginPct: "",
};

// Margin/bonus defaults by number of drops: single drop 25%, multi-drop 40% + $100 per drop.
export const SINGLE_DROP_MARGIN = 25;
export const MULTI_DROP_MARGIN = 40;
export const MULTI_DROP_BONUS_PER_DROP = 100;
export const MAX_DRIVE_HOURS_PER_DAY = 10;

// --- Broker: market rate per one-way mile (just to get there); margin is optional ---
function computeBroker(ci, oneWayMiles) {
  const rate = Number(ci.brokerRatePerMile) || 0;
  const base = rate * oneWayMiles;
  const marginPct = Number(ci.brokerMarginPct) || 0;
  const margin = base * (marginPct / 100);
  const price = base + margin;
  return {
    key: "broker",
    label: "Broker",
    available: base > 0,
    price,
    perStop: price,
    breakdown: [
      { label: `Market rate (${oneWayMiles.toFixed(1)} mi one-way × ${fmtMoney(rate)}/mi)`, amount: base },
      ...(margin > 0 ? [{ label: `Margin (${marginPct}%)`, amount: margin }] : []),
    ],
  };
}

// --- In-house: fuel + labor + wear + overnight - backhaul, marked up; bonus paid from margin ---
function computeInhouse({ costInputs: ci, overnight, backhaul, bonus }, N, miles, marginRate) {
  const available =
    miles > 0 && Number(ci.mpg) > 0 && Number(ci.fuelPrice) > 0 &&
    Number(ci.wage) > 0 && Number(ci.avgSpeed) > 0;
  const fuelCost = (miles / (Number(ci.mpg) || 1)) * (Number(ci.fuelPrice) || 0);
  const driveHours = miles / (Number(ci.avgSpeed) || 1);
  const stopHours = ((Number(ci.stopTimeMin) || 0) * N) / 60;
  const laborCost = (driveHours + stopHours) * (Number(ci.wage) || 0);
  const wearCost = miles * (Number(ci.wearTear) || 0);
  const nights = Math.max(1, Math.round(Number(overnight.nights) || 1));
  const overnightCost = overnight.enabled
    ? ((Number(overnight.hotelCost) || 0) + (Number(overnight.mealPerDiem) || 0)) * nights
    : 0;
  const backhaulRev = backhaul.enabled ? Number(backhaul.revenue) || 0 : 0;
  const netCost = fuelCost + laborCost + wearCost + overnightCost - backhaulRev;
  const margin = netCost * marginRate;
  const price = netCost + margin;
  // Flat bonus = per-drop amount × number of drops (legacy quotes may only have flatAmount).
  const flatBonus =
    bonus.perDrop != null
      ? (Number(bonus.perDrop) || 0) * Math.max(1, Number(bonus.drops) || 1)
      : Number(bonus.flatAmount) || 0;
  const bonusAmount =
    bonus.type === "pct" ? margin * ((Number(bonus.pctAmount) || 0) / 100) : flatBonus;
  const totalRouteHours = driveHours + stopHours;
  return {
    key: "inhouse",
    label: "In-House Truck",
    available,
    price,
    perStop: N > 0 ? price / N : price,
    netMargin: margin - bonusAmount,
    bonusAmount,
    totalRouteHours,
    breakdown: [
      { label: `Fuel (${miles} mi ÷ ${ci.mpg} mpg × ${fmtMoney(Number(ci.fuelPrice) || 0)})`, amount: fuelCost },
      { label: `Labor (${totalRouteHours.toFixed(1)} hrs × ${fmtMoney(Number(ci.wage) || 0)})`, amount: laborCost },
      { label: `Wear & tear (${miles} mi × ${fmtMoney(Number(ci.wearTear) || 0)})`, amount: wearCost },
      ...(overnightCost > 0 ? [{ label: `Overnight (${nights} night${nights > 1 ? "s" : ""} × hotel + per diem)`, amount: overnightCost }] : []),
      ...(backhaulRev > 0 ? [{ label: "Backhaul revenue", amount: -backhaulRev }] : []),
      { label: `Margin (${ci.marginPct || 0}%)`, amount: margin },
      ...(bonusAmount > 0 ? [{ label: "Driver bonus (from margin)", amount: -bonusAmount, muted: true }] : []),
    ],
  };
}

// --- Outsourced: cheapest of up to 3 carrier bids wins; margin is optional ---
function computeOutsourced(outsourced, N) {
  const bids = (outsourced.bids || [])
    .filter((b) => Number(b.rate) > 0)
    .map((b) => ({ id: b.id, carrierName: (b.carrierName || "").trim(), rate: Number(b.rate) }))
    .sort((a, b) => a.rate - b.rate);
  const best = bids[0];
  const base = best ? best.rate : 0;
  const marginPct = Number(outsourced.marginPct) || 0;
  const margin = base * (marginPct / 100);
  const price = base + margin;
  return {
    key: "outsourced",
    label: best?.carrierName ? `Outsourced — ${best.carrierName}` : "Outsourced Carrier",
    available: base > 0,
    price,
    perStop: N > 0 ? price / N : price,
    carrierName: best ? best.carrierName : "",
    otherBids: bids.slice(1),
    breakdown: [
      { label: best?.carrierName ? `Winning bid — ${best.carrierName}` : "Carrier bid", amount: base },
      ...(margin > 0 ? [{ label: `Margin (${marginPct}%)`, amount: margin }] : []),
    ],
  };
}

function pickRecommendation(candidates) {
  const options = candidates.filter((o) => o.available).sort((a, b) => a.price - b.price);
  if (!options.length) return null;
  const [cheapest, next] = options;
  return {
    method: cheapest.label,
    key: cheapest.key,
    price: cheapest.price,
    diff: next ? next.price - cheapest.price : null,
    nextMethod: next ? next.label : null,
  };
}

// Core three-way comparison math.
export function computeComparison(i) {
  const N = Math.max(0, i.numStops || 0);
  const ci = i.costInputs;
  const marginRate = (Number(ci.marginPct) || 0) / 100;
  const miles = Number(i.totalMiles) || 0;
  const oneWayMiles = Number(i.oneWayMiles) || 0;

  const broker = computeBroker(ci, oneWayMiles);
  const inhouse = computeInhouse(i, N, miles, marginRate);
  const outsourced = computeOutsourced(i.outsourced, N);
  const recommendation = pickRecommendation([broker, inhouse, outsourced]);

  return { broker, inhouse, outsourced, recommendation, numStops: N, totalRouteHours: inhouse.totalRouteHours };
}
